/**
 * Owns every piece of Home page forms-table state: the table store, the server
 * query, the active tab, the saved views and their modals. Consumed by
 * `HomePageContent` and, through it, by the whole toolbar.
 *
 * `settings.homePageViewId` is the id of the My View *document*, not a pointer
 * at some other promoted view: My View is an ordinary `homePageViews` doc that
 * owns a reserved tab, and it does not exist until the user first saves it.
 * `activeViewKind`/`activeSavedViewId`/`activeViewName` are what the contextual
 * view menu acts on, so every view command works from the My View tab too.
 *
 * Every filter source — the header cells, the persisted state, a saved view, a report's
 * defaults — converges on the `where` array built here, so this is the one place that can
 * see the whole query. A `where` over `FIRESTORE_MAX_DISJUNCTIONS` is never sent: the query
 * stays off, `store.queryDisabledReason` holds the reason for as long as it applies so the
 * table shows it in place of rows, and a toast names the columns that multiply once per
 * distinct reason — rather than values being silently dropped (SYD-2574).
 */
import { useMemo, useRef, useState, useContext, useCallback, useEffect } from 'react';
import { doc, collection, deleteField } from 'firebase/firestore';
import { subscribe, snapshot } from 'valtio';
import debounce from 'lodash/debounce';
import type { HomeFormVersionsTableHandle } from '../../HomeFormVersionsTable';
import { formVersionsConfig } from '../../../docList/configs/formVersionsConfig';
import { useDocListData } from '../../../docList/useDocListData';
import { UserContext } from '../../../../../providers/UserContext';
import { MessageContext } from '../../../../../providers/MessageContext';
import { firestore } from '../../../../../services/startFirestore';
import { useSet } from '../../../../../use/data/useSet';
import { useRemove } from '../../../../../use/data/useRemove';
import { useHomeFormVersionsDynamicColumns } from '../../HomeFormVersionsTable/use';
import { homePageConfig, defaultMetricStartTimeWindow } from '#types';
import type {
  DocListNamedView,
  DocListViewSettingsId,
  FormVersionWithPath,
  ListFormVersionsDocsRequest,
} from '#types';
import { exportTableToExcel } from '../../../../exportTable/exportTableToExcel';
import {
  createDocListTableStore,
} from '../../../docList/createDocListTableStore';
import createFilterConflictResolver from '../../../docList/createFilterCombinationGuard';
import { countWhereDisjunctions } from '../../../docList/countWhereDisjunctions';
import { FIRESTORE_MAX_DISJUNCTIONS } from '../../../docList/DocListTable/constants';
import type { ColumnSpec } from '../../../docList/DocList.types';
import { useHomePageSearch } from '../useHomePageSearch';
import { useHomePageReportCounts } from '../useHomePageReportCounts';
import { useHomePageSavedViews } from '../useHomePageSavedViews';
import type { HomePageToolbarViewType } from '../../HomePageToolbar/HomePageToolbar';
import {
  areReportFiltersPresent,
  buildDefaultVisibility,
  buildServerWhereFromStore,
  buildServerOrderByFromStore,
  hasExtraServerFilters,
  resolveInitialFilters,
} from './homePageStateFns';
import { loadPersistedState, persistState } from './persistence';

type TableViewNameModalMode = 'saveAs' | 'rename';

type TableViewNameModalState = {
  open: boolean;
  mode: TableViewNameModalMode;
  value: string;
};

interface UseHomePageOrchestratorParams {
  tableRef: React.RefObject<HomeFormVersionsTableHandle | null>;
}

/**
 * Names the columns whose selected values multiply past the query's disjunction budget,
 * in the words the table header shows, or returns null while the `where` array fits.
 */
const describeFilterBudgetOverrun = (
  where: NonNullable<ListFormVersionsDocsRequest['where']>,
  columns: ColumnSpec<FormVersionWithPath>[],
): string | null => {
  const total = countWhereDisjunctions(where);
  if (total <= FIRESTORE_MAX_DISJUNCTIONS) return null;

  const factors = where.map((clause) => {
    const column = columns.find((c) => c.serverFilterField === clause.fieldPath);
    const count = Array.isArray(clause.value) ? clause.value.length : 1;
    return `${column?.header ?? clause.fieldPath} (${count})`;
  });

  return `Filters multiply: ${factors.join(' × ')} is ${total} combinations, over the limit of ${FIRESTORE_MAX_DISJUNCTIONS} — narrow one of them.`;
};

const useHomePageOrchestrator = ({ tableRef }: UseHomePageOrchestratorParams) => {
  const { user, settings } = useContext(UserContext);
  const { messageApi } = useContext(MessageContext);
  const { set } = useSet();
  const { remove } = useRemove();
  const configWithDynamicColumns = useHomeFormVersionsDynamicColumns();
  const resolveFilterConflicts = useMemo(
    () => createFilterConflictResolver(
      configWithDynamicColumns.columns,
      configWithDynamicColumns.allowedFilterCombinations,
    ),
    [configWithDynamicColumns],
  );

  const defaultBaseView = homePageConfig.formsTable.reports[0]?.id ?? 'myTasks';
  const persisted = useMemo(() => user?.uid ? loadPersistedState(user.uid) : null, []); // eslint-disable-line react-hooks/exhaustive-deps

  const [store] = useState(() => {
    const s = createDocListTableStore({
      sorting: persisted?.sorting ?? formVersionsConfig.defaultSorting ?? [],
      filters: persisted?.columnFilters ?? resolveInitialFilters(persisted?.baseFormView ?? defaultBaseView, user?.uid),
      visibility: persisted?.columnVisibility ?? buildDefaultVisibility(),
    });
    if (persisted) {
      if (persisted.columnOrder.length > 0) s.columnOrder = [...persisted.columnOrder];
      if (Object.keys(persisted.columnSizing).length > 0) s.columnSizing = { ...persisted.columnSizing };
      if (Object.keys(persisted.columnStatistics).length > 0) s.columnStatistics = { ...persisted.columnStatistics };
    }
    return s;
  });

  const {
    searchQuery,
    setSearchQuery,
    searchResults,
    isSearching,
    isSearchActive,
    resultCount,
    clearSearch,
  } = useHomePageSearch();

  const baseDateParams = useMemo(() => {
    const now = new Date();
    const preset = homePageConfig.formsTable.defaultDateRangePreset;

    const startDate = (() => {
      switch (preset) {
        case 'yearToDate': {
          return new Date(now.getFullYear(), 0, 1);
        }
        case 'quarterToDate': {
          const quarterStartMonth = Math.floor(now.getMonth() / 3) * 3;
          return new Date(now.getFullYear(), quarterStartMonth, 1);
        }
        case 'monthToDate': {
          return new Date(now.getFullYear(), now.getMonth(), 1);
        }
        case 'weekToDate': {
          const d = new Date(now);
          const day = d.getDay();
          const mondayOffset = (day + 6) % 7;
          d.setDate(d.getDate() - mondayOffset);
          d.setHours(0, 0, 0, 0);
          return d;
        }
        case 'allTime': {
          return new Date(defaultMetricStartTimeWindow);
        }
        default: {
          return new Date(now.getFullYear(), 0, 1);
        }
      }
    })();

    return {
      startDate: {
        _seconds: Math.floor(startDate.getTime() / 1000),
        _nanoseconds: 0,
      },
      endDate: {
        _seconds: Math.floor(now.getTime() / 1000),
        _nanoseconds: 0,
      },
    };
  }, []);

  const latestRef = useRef({
    columns: configWithDynamicColumns.columns,
    baseFormView: persisted?.baseFormView ?? defaultBaseView,
    activeView: (persisted?.activeView ?? defaultBaseView) as string,
  });
  latestRef.current.columns = configWithDynamicColumns.columns;

  store.resolveFilterConflicts = resolveFilterConflicts; // eslint-disable-line react-hooks/immutability

  const lastFilterBudgetWarning = useRef<string | null>(null);

  const [serverFilters, setServerFilters] = useState<{
    where?: NonNullable<ListFormVersionsDocsRequest['where']>;
    orderBy?: Array<{ fieldPath: string; direction: 'asc' | 'desc' }>;
  }>(() => {
    const snap = snapshot(store);
    const where = buildServerWhereFromStore(snap, configWithDynamicColumns.columns);
    const extraFilters = hasExtraServerFilters(snap, configWithDynamicColumns.columns, latestRef.current.baseFormView);
    const orderBy = extraFilters ? undefined : buildServerOrderByFromStore(snap, configWithDynamicColumns.columns);
    return {
      where: where.length > 0 ? where : undefined,
      orderBy,
    };
  });

  /** Re-runs the server-filter sync when a dynamic column gains a sort field, which
   *  happens after `useFormTypes` resolves and `field_<id>` columns first appear. */
  const serverSortFieldsKey = configWithDynamicColumns.columns
    .map((column) => `${column.id}:${column.serverSortField ?? ''}`)
    .join('|');

  useEffect(() => {
    /* eslint-disable react-hooks/immutability */
    const sync = () => {
      const snap = snapshot(store);
      const { columns: cols, baseFormView: currentView, activeView: currentActive } = latestRef.current;
      const where = buildServerWhereFromStore(snap, cols);
      const extraFilters = hasExtraServerFilters(snap, cols, currentView);
      const orderBy = extraFilters ? undefined : buildServerOrderByFromStore(snap, cols);

      const sortDisabledReason = isSearchActive
        ? 'Sort is unavailable while a search is active'
        : extraFilters
          ? 'Sort is unavailable with this combination of filters'
          : null;

      if (store.sortDisabledReason !== sortDisabledReason) {
        store.sortDisabledReason = sortDisabledReason;
      }

      const budgetWarning = describeFilterBudgetOverrun(where, cols);

      if (store.queryDisabledReason !== budgetWarning) {
        store.queryDisabledReason = budgetWarning;
      }

      if (currentActive === currentView && !areReportFiltersPresent(snap, currentView)) {
        latestRef.current.activeView = 'favorites';
        setActiveView('favorites');
        setSelectedCustomViewId(undefined);
      }

      /* eslint-enable react-hooks/immutability */
      if (budgetWarning !== lastFilterBudgetWarning.current) {
        lastFilterBudgetWarning.current = budgetWarning;
        if (budgetWarning) messageApi.warning(budgetWarning);
      }

      setServerFilters((prev) => {
        const next = {
          where: where.length > 0 ? where : undefined,
          orderBy,
        };
        if (JSON.stringify(prev) === JSON.stringify(next)) return prev;
        return next;
      });

      if (user?.uid) {
        persistState(user.uid, {
          version: 1,
          activeView: latestRef.current.baseFormView,
          baseFormView: latestRef.current.baseFormView,
          selectedCustomViewId: undefined,
          columnFilters: snap.columnFilters.map((f) => ({ id: f.id, value: f.value })),
          sorting: snap.sorting.map((s) => ({ id: s.id, desc: s.desc })),
          columnVisibility: { ...snap.columnVisibility },
          columnSizing: { ...snap.columnSizing },
          columnOrder: [...snap.columnOrder],
          columnStatistics: { ...snap.columnStatistics },
        });
      }
    };

    sync();

    const debouncedSync = debounce(sync, 300);
    const unsubscribe = subscribe(store, debouncedSync);

    return () => {
      unsubscribe();
      debouncedSync.cancel();
    };
  }, [store, user?.uid, serverSortFieldsKey, isSearchActive, messageApi]);

  const formsTableQueryParams = useMemo(() => ({
    ...baseDateParams,
    ...serverFilters,
  }), [baseDateParams, serverFilters]);

  const isWithinFilterBudget =
    countWhereDisjunctions(serverFilters.where ?? []) <= FIRESTORE_MAX_DISJUNCTIONS;

  const handleDataError = useCallback(() => {
    messageApi.error('Failed to load data');
  }, [messageApi]);

  const handleReportCountsError = useCallback(() => {
    messageApi.error('Failed to load report counts');
  }, [messageApi]);

  const {
    data: formVersions,
    isLoading: formVersionsLoading,
    isLoadingMore,
    hasMore,
    loadMore,
  } = useDocListData({
    dataSource: formVersionsConfig.dataSource,
    queryParams: formsTableQueryParams,
    enabled: isWithinFilterBudget,
    onError: handleDataError,
  });

  store.isLoading = formVersionsLoading || isSearching; // eslint-disable-line react-hooks/immutability

  const effectiveData = isSearchActive ? searchResults : formVersions;
  const effectiveHasMore = isSearchActive ? false : hasMore;
  const effectiveIsLoadingMore = isSearchActive ? false : isLoadingMore;

  const reportCounts = useHomePageReportCounts({
    formsTableQueryParams: baseDateParams,
    userId: user?.uid,
    onError: handleReportCountsError,
  });

  const viewSettingsId: DocListViewSettingsId = formVersionsConfig.viewSettingsId;

  const { customViews, isLoading: savedViewsLoading } = useHomePageSavedViews({
    userId: user?.uid,
    viewSettingsId,
  });

  const [activeView, setActiveView] = useState<HomePageToolbarViewType>(persisted?.activeView ?? defaultBaseView);
  latestRef.current.activeView = activeView as string;
  const [baseFormView, setBaseFormView] = useState<string>(persisted?.baseFormView ?? defaultBaseView);
  const [selectedCustomViewId, setSelectedCustomViewId] = useState<string | undefined>(persisted?.selectedCustomViewId);
  const [tableViewNameModal, setTableViewNameModal] = useState<TableViewNameModalState>({
    open: false,
    mode: 'saveAs',
    value: '',
  });
  const [isDeleteSavedViewModalOpen, setIsDeleteSavedViewModalOpen] = useState(false);
  const [isDeletingSavedView, setIsDeletingSavedView] = useState(false);

  const selectedCustomView = useMemo(() => {
    if (!selectedCustomViewId) return undefined;
    return customViews.find((view) => view.id === selectedCustomViewId);
  }, [customViews, selectedCustomViewId]);

  const myViewTargetId = settings?.homePageViewId;

  const myViewTarget = useMemo(
    () => customViews.find((view) => view.id === myViewTargetId),
    [customViews, myViewTargetId],
  );

  const myViewLabel = myViewTarget?.name ?? 'My View';

  const activeViewKind: 'myView' | 'savedView' | 'report' = (() => {
    if (activeView === 'myView') return 'myView';
    if (activeView === 'favorites' && selectedCustomViewId) return 'savedView';
    return 'report';
  })();

  const activeSavedViewId = (() => {
    if (activeViewKind === 'myView') return myViewTargetId;
    if (activeViewKind === 'savedView') return selectedCustomViewId;
    return undefined;
  })();

  const activeViewName = (() => {
    if (activeViewKind === 'myView') return myViewLabel;
    if (activeViewKind === 'savedView') return selectedCustomView?.name ?? 'Custom';
    return homePageConfig.formsTable.reports.find((report) => report.id === activeView)?.label ?? 'Custom';
  })();

  const persistViewState = useCallback((nextActiveView: string, nextBaseFormView: string, nextCustomViewId?: string) => {
    if (!user?.uid) return;
    const snap = snapshot(store);
    persistState(user.uid, {
      version: 1,
      activeView: nextActiveView,
      baseFormView: nextBaseFormView,
      selectedCustomViewId: nextCustomViewId,
      columnFilters: snap.columnFilters.map((f) => ({ id: f.id, value: f.value })),
      sorting: snap.sorting.map((s) => ({ id: s.id, desc: s.desc })),
      columnVisibility: { ...snap.columnVisibility },
      columnSizing: { ...snap.columnSizing },
      columnOrder: [...snap.columnOrder],
      columnStatistics: { ...snap.columnStatistics },
    });
  }, [store, user?.uid]);

  const handleBaseViewBecameDirty = useCallback(() => {
    const isBaseViewActive = activeView === baseFormView;
    if (!isBaseViewActive) return;
    setActiveView('favorites');
    setSelectedCustomViewId(undefined);
  }, [activeView, baseFormView]);

  const handleNamedViewBecameDirty = useCallback(() => {
    if (!selectedCustomViewId) return;
    if (activeView !== 'favorites') return;
  }, [selectedCustomViewId, activeView]);

  const handleViewChange = useCallback((view: HomePageToolbarViewType) => {
    if (isSearchActive) clearSearch();
    if (typeof view === 'string' && homePageConfig.formsTable.reports.some((r) => r.id === view)) {
      const shouldReset = baseFormView === view;
      setActiveView(view);
      setSelectedCustomViewId(undefined);
      setBaseFormView(view);
      latestRef.current.baseFormView = view;
      if (shouldReset) {
        tableRef.current?.resetToDefaultView();
      } else {
        tableRef.current?.applyBaseView(view);
      }
      persistViewState(view, view, undefined);
      return;
    }

    if (view === 'favorites') {
      setActiveView('favorites');
      return;
    }

    setActiveView(view);
  }, [isSearchActive, clearSearch, baseFormView, tableRef, persistViewState]);

  const handleCustomViewSelect = useCallback((viewId: string) => {
    const view = customViews.find((v) => v.id === viewId);
    if (!view) {
      messageApi.error('View not found');
      return;
    }

    setSelectedCustomViewId(viewId);
    setActiveView('favorites');
    tableRef.current?.applyTableViewSettings(view.settings);
    persistViewState('favorites', baseFormView, viewId);
  }, [customViews, messageApi, tableRef, persistViewState, baseFormView]);

  const landOnMyView = useCallback((target: DocListNamedView | undefined) => {
    setActiveView('myView');
    latestRef.current.activeView = 'myView';

    if (target) {
      setSelectedCustomViewId(target.id);
      tableRef.current?.applyTableViewSettings(target.settings);
      persistViewState('myView', baseFormView, target.id);
      return;
    }

    setSelectedCustomViewId(undefined);
    setBaseFormView('myTasks');
    latestRef.current.baseFormView = 'myTasks';
    tableRef.current?.applyBaseView('myTasks');
    persistViewState('myView', 'myTasks', undefined);
  }, [baseFormView, tableRef, persistViewState]);

  const handleSelectMyView = useCallback(() => {
    landOnMyView(myViewTarget);
  }, [landOnMyView, myViewTarget]);

  const myViewLandedRef = useRef(false);

  useEffect(() => {
    if (myViewLandedRef.current) return;
    if (savedViewsLoading) return;
    myViewLandedRef.current = true;

    if (myViewTarget) {
      handleSelectMyView();
      return;
    }

    setActiveView('myView');
    latestRef.current.activeView = 'myView';
  }, [savedViewsLoading, myViewTarget, handleSelectMyView]);

  const handleSetMyView = useCallback(async (viewId?: string) => {
    if (!user?.uid) {
      messageApi.error('You must be logged in to set My View');
      return;
    }

    const target = viewId ? customViews.find((view) => view.id === viewId) : undefined;

    try {
      await set('users', user.uid, { settings: { homePageViewId: viewId ?? deleteField() } });
    } catch (e) {
      console.error('Failed to set My View', e);
      messageApi.error('Failed to set My View');
      return;
    }

    messageApi.success(viewId ? 'Set as My View' : 'Reset My View');
    landOnMyView(target);
  }, [user?.uid, customViews, set, messageApi, landOnMyView]);

  const handleAddColumn = useCallback(() => {
    tableRef.current?.openAddColumnPicker();
  }, [tableRef]);

  const handleClearAllFilters = useCallback(() => {
    tableRef.current?.clearAllFilters();
    setActiveView('favorites');
    setSelectedCustomViewId(undefined);
    persistViewState('favorites', baseFormView, undefined);
  }, [tableRef, persistViewState, baseFormView]);

  const openSaveAsNewViewModal = useCallback(() => {
    setTableViewNameModal({
      open: true,
      mode: 'saveAs',
      value: '',
    });
  }, []);

  const handleRenameActiveView = useCallback(() => {
    if (!activeSavedViewId) {
      messageApi.error('No saved view selected');
      return;
    }
    setTableViewNameModal({
      open: true,
      mode: 'rename',
      value: activeViewName,
    });
  }, [activeSavedViewId, activeViewName, messageApi]);

  const closeTableViewNameModal = useCallback(() => {
    setTableViewNameModal((prev) => ({
      ...prev,
      open: false,
    }));
  }, []);

  const setTableViewNameModalValue = useCallback((value: string) => {
    setTableViewNameModal((prev) => ({ ...prev, value }));
  }, []);

  const handleConfirmTableViewName = useCallback(async () => {
    const name = tableViewNameModal.value.trim();
    if (name.length === 0) {
      messageApi.error('Please enter a view name');
      return;
    }

    if (!user?.uid) {
      messageApi.error('You must be logged in to save views');
      return;
    }

    if (tableViewNameModal.mode === 'rename') {
      const viewId = activeSavedViewId;
      if (!viewId) {
        messageApi.error('No saved view selected');
        return;
      }

      try {
        await set(`users/${user.uid}/homePageViews`, viewId, {
          name,
        });
      } catch (e) {
        console.error('Failed to rename view', e);
        messageApi.error('Failed to rename table view');
        return;
      }

      closeTableViewNameModal();
      messageApi.success('Renamed table view');
      return;
    }

    const snapshotData = tableRef.current?.getViewSettingsSnapshot();
    if (!snapshotData) {
      messageApi.error('Table is not ready yet');
      return;
    }

    try {
      const docRef = doc(collection(firestore, `users/${user.uid}/homePageViews`));
      const viewId = docRef.id;

      await set(`users/${user.uid}/homePageViews`, viewId, {
        viewSettingsId,
        name,
        settings: snapshotData,
      });

      setSelectedCustomViewId(viewId);
      setActiveView('favorites');
      setTableViewNameModal((prev) => ({
        ...prev,
        open: false,
        value: '',
      }));
      messageApi.success('Saved table view');
      persistViewState('favorites', baseFormView, viewId);
    } catch (e) {
      console.error('Failed to save view', e);
      messageApi.error('Failed to save table view');
    }
  }, [tableViewNameModal, user?.uid, activeSavedViewId, set, closeTableViewNameModal, messageApi, tableRef, viewSettingsId, persistViewState, baseFormView]);

  const writeViewSettings = useCallback(async (viewId: string) => {
    if (!user?.uid) {
      messageApi.error('You must be logged in to save views');
      return;
    }

    const snapshotData = tableRef.current?.getViewSettingsSnapshot();
    if (!snapshotData) {
      messageApi.error('Table is not ready yet');
      return;
    }

    try {
      await set(`users/${user.uid}/homePageViews`, viewId, {
        viewSettingsId,
        settings: snapshotData,
        meta: {
          created: customViews.find((view) => view.id === viewId)?.meta?.createdAt as any,
        },
      });
      messageApi.success('Saved table view');
    } catch (e) {
      console.error('Failed to save view', e);
      messageApi.error('Failed to save table view');
    }
  }, [user?.uid, customViews, set, messageApi, tableRef, viewSettingsId]);

  const handleSaveActiveView = useCallback(async () => {
    if (activeViewKind === 'report') return;

    if (!user?.uid) {
      messageApi.error('You must be logged in to save views');
      return;
    }

    if (activeViewKind === 'myView' && !myViewTargetId) {
      const snapshotData = tableRef.current?.getViewSettingsSnapshot();
      if (!snapshotData) {
        messageApi.error('Table is not ready yet');
        return;
      }

      const newViewId = doc(collection(firestore, `users/${user.uid}/homePageViews`)).id;

      try {
        await set(`users/${user.uid}/homePageViews`, newViewId, {
          viewSettingsId,
          name: 'My View',
          settings: snapshotData,
        });
        await set('users', user.uid, { settings: { homePageViewId: newViewId } });
      } catch (e) {
        console.error('Failed to save My View', e);
        messageApi.error('Failed to save table view');
        return;
      }

      setSelectedCustomViewId(newViewId);
      persistViewState('myView', baseFormView, newViewId);
      messageApi.success('Saved My View');
      return;
    }

    if (!activeSavedViewId) {
      messageApi.error('No saved view selected');
      return;
    }

    await writeViewSettings(activeSavedViewId);
  }, [activeViewKind, activeSavedViewId, myViewTargetId, user?.uid, set, messageApi, tableRef, viewSettingsId, persistViewState, baseFormView, writeViewSettings]);

  const handleResetActiveView = useCallback(() => {
    if (activeViewKind !== 'report') {
      const target = customViews.find((view) => view.id === activeSavedViewId);
      if (target) {
        tableRef.current?.applyTableViewSettings(target.settings);
        return;
      }
      if (activeViewKind === 'myView') {
        tableRef.current?.applyBaseView('myTasks');
        return;
      }
    }
    tableRef.current?.applyBaseView(baseFormView);
  }, [activeViewKind, activeSavedViewId, customViews, tableRef, baseFormView]);

  const handleDeleteActiveView = useCallback(() => {
    if (!user?.uid) {
      messageApi.error('You must be logged in to delete views');
      return;
    }

    if (!activeSavedViewId) {
      messageApi.error('No saved view selected');
      return;
    }
    setIsDeleteSavedViewModalOpen(true);
  }, [user?.uid, activeSavedViewId, messageApi]);

  const handleSetActiveViewAsMyView = useCallback(() => {
    if (!activeSavedViewId) {
      messageApi.error('No saved view selected');
      return;
    }
    void handleSetMyView(activeSavedViewId);
  }, [activeSavedViewId, handleSetMyView, messageApi]);

  const handleConfirmDeleteSavedView = useCallback(async () => {
    if (!user?.uid) {
      messageApi.error('You must be logged in to delete views');
      return;
    }

    if (!activeSavedViewId) {
      messageApi.error('No saved view selected');
      return;
    }

    setIsDeletingSavedView(true);
    try {
      await remove(`users/${user.uid}/homePageViews`, activeSavedViewId);
    } catch (e) {
      console.error('Failed to delete saved view', e);
      messageApi.error('Failed to delete saved view');
      setIsDeletingSavedView(false);
      return;
    }

    if (activeSavedViewId === myViewTargetId) {
      try {
        await set('users', user.uid, { settings: { homePageViewId: deleteField() } });
      } catch (e) {
        console.error('Failed to reset My View', e);
      }
    }

    setSelectedCustomViewId(undefined);
    setActiveView('favorites');
    setIsDeletingSavedView(false);
    setIsDeleteSavedViewModalOpen(false);
    messageApi.success('Deleted saved view');
    persistViewState('favorites', baseFormView, undefined);
  }, [user?.uid, activeSavedViewId, myViewTargetId, remove, set, messageApi, persistViewState, baseFormView]);

  const closeDeleteSavedViewModal = useCallback(() => {
    if (isDeletingSavedView) return;
    setIsDeleteSavedViewModalOpen(false);
  }, [isDeletingSavedView]);

  const exportViewLabel = useMemo(() => {
    const reports = homePageConfig.formsTable.reports;
    const activeLabel = reports.find((r) => r.id === activeView)?.label;
    if (activeLabel) return activeLabel;

    if (activeView === 'favorites') {
      if (selectedCustomView?.name) return selectedCustomView.name;
      return 'Custom';
    }

    return 'Home';
  }, [activeView, selectedCustomView]);

  const handleExportTable = useCallback(async () => {
    const exportSnap = tableRef.current?.getExportSnapshot();
    if (!exportSnap) {
      messageApi.warning('Table is not ready yet');
      return;
    }

    if (exportSnap.tableValue.rows.length === 0) {
      messageApi.warning('No data to export');
      return;
    }

    if (exportSnap.columns.length === 0) {
      messageApi.warning('No columns to export');
      return;
    }

    const result = await exportTableToExcel(exportSnap.tableValue, exportSnap.columns, {
      filenameLabel: exportViewLabel,
    });

    if (!result.success) {
      messageApi.warning(result.message);
    }
  }, [exportViewLabel, messageApi, tableRef]);

  const tableViewNameModalTitle =
    tableViewNameModal.mode === 'rename' ? 'Rename Table View' : 'Save as New View';

  const tableViewNameModalOkText = tableViewNameModal.mode === 'rename' ? 'Rename' : 'Save';

  return {
    store,
    configWithDynamicColumns,

    searchQuery,
    setSearchQuery,
    isSearching,
    isSearchActive,
    resultCount,
    clearSearch,

    effectiveData,
    effectiveHasMore,
    effectiveIsLoadingMore,
    loadMore,

    reportCounts,

    activeView,
    baseFormView,
    selectedCustomViewId,
    customViews,
    activeNamedViewSettings: selectedCustomView?.settings ?? null,
    myViewTargetId,
    myViewTarget,
    myViewLabel,
    activeViewKind,
    activeSavedViewId,
    activeViewName,

    handleViewChange,
    handleCustomViewSelect,
    handleSelectMyView,
    handleSetMyView,
    handleAddColumn,
    handleClearAllFilters,
    handleBaseViewBecameDirty,
    handleNamedViewBecameDirty,

    tableViewNameModal,
    setTableViewNameModalValue,
    isDeleteSavedViewModalOpen,
    isDeletingSavedView,

    openSaveAsNewViewModal,
    closeTableViewNameModal,
    handleConfirmTableViewName,
    handleSaveActiveView,
    handleRenameActiveView,
    handleResetActiveView,
    handleDeleteActiveView,
    handleSetActiveViewAsMyView,
    handleConfirmDeleteSavedView,
    closeDeleteSavedViewModal,

    handleExportTable,
    tableViewNameModalTitle,
    tableViewNameModalOkText,
  };
};

export type HomePageOrchestrator = ReturnType<typeof useHomePageOrchestrator>;

export default useHomePageOrchestrator;
